/**
 * Vue Router beforeEach guard — blocks navigation to a route whose
 * `meta.feature` (or `meta.features[]` for multiple) is disabled.
 *
 * Fail-closed: if the feature is missing from the resolved map AND
 * we know the map is loaded (`featuresLoadedAt > 0`), the route is
 * blocked. Before login the guard is permissive so the auth screens
 * stay reachable.
 */
import { watch } from "vue";
import type { NavigationGuardWithThis, RouteLocationNormalized, Router } from "vue-router";
import { featuresMap, featuresLoadedAt } from "@/state/api/featuresState";
import { userInfos } from "@/state/api/userState";
import { isFeatureFlagEnabled } from "@/composable/useFeatureFlag";

// Page contextuelle « Module désactivé » avec message explicatif. On
// préfère cette route à `auth-blocked` (générique) parce qu'elle reçoit
// la clé feature en query et peut afficher le nom user-friendly du
// module concerné — meilleure UX que le panneau d'accès interdit.
const MODULE_DISABLED_ROUTE_NAME = "module-disabled";

/**
 * Aggrège les feature keys de TOUTES les routes matched, pas seulement
 * la route finale. Sans ça, le `meta.feature` d'un parent (ex.
 * `module.agencies` sur le segment `/agency`) est écrasé par le meta
 * d'un enfant (`feature.agencies.overview` sur `/agency/overview`)
 * via la fusion overwrite par défaut de Vue Router. Conséquence :
 * désactiver `module.agencies` côté Root cachait bien le sidebar
 * mais laissait l'URL directe `/workspace/agency/overview` passer
 * (la sub-feature étant default-enabled).
 *
 * En itérant sur `to.matched` on collecte toutes les contraintes —
 * les MODULE et les FEATURE — et on les vérifie toutes (AND).
 */
function requiredKeys(to: RouteLocationNormalized): string[] {
	const out = new Set<string>();
	for (const record of to.matched) {
		const single = record.meta?.feature as string | undefined;
		const multi = record.meta?.features as string[] | undefined;
		if (single) out.add(single);
		if (Array.isArray(multi)) multi.forEach((k) => k && out.add(k));
	}
	return Array.from(out);
}

function isRootUser(): boolean {
	const roles = userInfos.value?.roles;
	if (!Array.isArray(roles)) return false;
	return roles.some((r: any) => r?.name === "Root" || Number(r?.id) === 4);
}

export const featureGuard: NavigationGuardWithThis<undefined> = (to, _from, next) => {
	// No features required → pass through
	const keys = requiredKeys(to);
	if (keys.length === 0) return next();

	// Root bypasses every feature check (mirrors backend Features facade
	// behaviour — Root has no enterprise scope so the cascade can't resolve).
	if (isRootUser()) return next();

	// Features map not yet loaded (pre-login or hydrating) → permissive
	if (featuresLoadedAt.value === 0 || Object.keys(featuresMap.value).length === 0) {
		return next();
	}

	// All keys must be enabled. Si l'une d'elles est OFF, on garde la
	// PREMIÈRE clé bloquante pour la passer en query à la page module-
	// disabled — qui affichera "Module Agences non actif" plutôt qu'un
	// message générique.
	const blockingKey = keys.find((k) => !isFeatureFlagEnabled(k));
	if (!blockingKey) return next();

	return next({
		name: MODULE_DISABLED_ROUTE_NAME,
		query: { feature: blockingKey, from: to.fullPath },
	});
};

/**
 * Surveille `featuresMap` pour rediriger vers la page module-disabled
 * quand la route courante devient bloquée APRÈS que l'utilisateur y
 * soit arrivé. Cas typique : Admin déjà connecté sur /workspace/agency
 * pendant que Root désactive `module.agencies` — sans ce watcher, la
 * page reste affichée avec des appels API qui retournent 403 et un
 * contenu vide (cf. screenshot du bug remonté).
 *
 * Appelé une fois après la création du router dans router/index.ts.
 */
export function setupFeatureWatcher(router: Router): void {
	const reEvaluate = () => {
		const current = router.currentRoute.value;
		if (!current) return;
		if (current.name === MODULE_DISABLED_ROUTE_NAME) return;
		if (isRootUser()) return;
		if (featuresLoadedAt.value === 0) return;

		const keys = requiredKeys(current);
		if (keys.length === 0) return;

		const blockingKey = keys.find((k) => !isFeatureFlagEnabled(k));
		if (!blockingKey) return;

		router
			.push({
				name: MODULE_DISABLED_ROUTE_NAME,
				query: { feature: blockingKey, from: current.fullPath },
			})
			.catch(() => {});
	};

	// 1) Watch reactif sur la map : capture le cas broadcast Reverb
	//    pendant que l'utilisateur est déjà sur la page.
	// 2) `afterEach` du router : capture le cas où la nav initiale
	//    s'est faite en mode permissif (featuresMap pas encore
	//    hydratée) et la map arrive juste après. Le watch suffit
	//    THÉORIQUEMENT mais on couple les deux pour blinder le
	//    timing — pas de risque de boucle puisque reEvaluate()
	//    bail sur la route module-disabled elle-même.
	watch(featuresMap, reEvaluate, { deep: true });
	watch(featuresLoadedAt, reEvaluate);
	router.afterEach(() => {
		// Re-check à chaque fin de navigation : si l'utilisateur arrive
		// sur une route bloquée pendant la phase permissive (map vide),
		// la map sera hydratée juste après et le watch se déclenchera.
		// L'afterEach prend le cas symétrique : map déjà chargée mais
		// nav précédente passée parce que le guard ne fait pas le check
		// sur les transitions internes synchrones.
		queueMicrotask(reEvaluate);
	});
}
